import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import {
    ClassificationView,
    ExistingControlResultClassification,
} from "api/generated/model";

export const PENDING_CLASS_TOKEN = "__Pending";
export const NO_CLASS_TOKEN = "__NoClass";
export const NO_MAPPING_CLASS_TOKEN = "__Missed";

/**
 * Pseudo-class tokens for the classification column. These are used in the classification
 * report, the report's own column uses these tokens, and the table filters match them.
 */
export const PSEUDO_CLASS_TOKENS = [
    PENDING_CLASS_TOKEN, // Control result is not yet completed
    NO_CLASS_TOKEN, // Completed but not mapping matched the task response
    NO_MAPPING_CLASS_TOKEN, // Not completed by due date
];

export function buildClassNamesById(
    classification: ClassificationView | null | undefined
): Map<string, string> {
    return new Map((classification?.classes ?? []).map((c) => [c.id, c.name]));
}

/**
 * An activity can hold several at once — a control with many classified answers
 * contributes to a class per answer — which is why the row value is a list and
 * the filter matches "has any of these".
 */
export function getClassificationClasses(
    classification: ExistingControlResultClassification | null | undefined,
    classNamesById: Map<string, string>
): string[] {
    if (!classification) {
        return [];
    }
    switch (classification.type) {
        case "Pending":
            return classification.taskCount > 0
                ? [PENDING_CLASS_TOKEN]
                : [NO_MAPPING_CLASS_TOKEN];
        case "Completed": {
            const named = Object.keys(classification.classified).map(
                (classId) => classNamesById.get(classId) ?? NO_CLASS_TOKEN
            );
            const withUnclassified =
                classification.unclassified > 0
                    ? named.concat([NO_CLASS_TOKEN])
                    : named;
            return withUnclassified.length > 0
                ? Array.from(new Set(withUnclassified))
                : [NO_MAPPING_CLASS_TOKEN];
        }
        default:
            return assertNever(classification);
    }
}

export function localizeClassificationClass(
    className: string,
    t: TFunction
): string {
    switch (className) {
        case PENDING_CLASS_TOKEN:
            return t("Report_Status_Pending");
        case NO_CLASS_TOKEN:
            return t("ControlActivitiesList_ClassificationFilter_NoClass");
        case NO_MAPPING_CLASS_TOKEN:
            // Same wording the report's own column uses.
            return t("Report_Status_MissedControlResults");
        default:
            return className;
    }
}
